"use client";

import { useState, useRef, useMemo, useCallback } from "react";
import { FaChevronLeft } from "react-icons/fa";
import { Filter, Mail, PenLine, Search, Trash2 } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn-components/ui/dropdown-menu";
import { useMailboxStore } from "@/vybe-trader-client/mailbox/stores/useMailboxStore";
import { useVybeTraderClient } from "@/vybe-trader-client/connection/hooks/useVybeTraderClient";
import {
  type MailRow,
  type MailboxFilterType,
  DirectionIcon,
  SeverityMark,
  filterMailRows,
  listPartyLabel,
  toMailRow,
} from "@/vybe-trader-client/mailbox/mailboxView";
import { ComposeMailScreen } from "../compose-mail";
import { MailDetailScreen } from "../mail-detail";
import { DENSITY } from "@/lib/density-scale";

interface MailboxScreenProps {
  onBack: () => void;
}

function SwipeableRow({
  children,
  onDelete,
}: {
  children: React.ReactNode;
  onDelete: () => void;
}) {
  const [startX, setStartX] = useState(0);
  const [offsetX, setOffsetX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    setStartX(e.touches[0].clientX);
    setDragging(true);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!dragging) return;
    const dx = e.touches[0].clientX - startX;
    if (dx < 0) setOffsetX(Math.max(dx, -72));
  };

  const onTouchEnd = () => {
    setDragging(false);
    if (offsetX < -40) setOffsetX(-72);
    else setOffsetX(0);
  };

  const handleDelete = () => {
    if (ref.current) {
      ref.current.style.transition = "opacity 0.2s ease-out";
      ref.current.style.opacity = "0";
      setTimeout(onDelete, 200);
    } else {
      onDelete();
    }
  };

  return (
    <div className="relative overflow-hidden border-b border-border">
      <Button
        type="button"
        variant="destructive"
        className="absolute right-0 top-0 h-full w-[72px] rounded-none"
        onClick={handleDelete}
        style={{ opacity: offsetX < -20 ? 1 : 0, pointerEvents: offsetX < -20 ? "auto" : "none" }}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
      <div
        ref={ref}
        className="relative bg-background transition-transform duration-200 ease-out"
        style={{ transform: `translateX(${offsetX}px)` }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {children}
      </div>
    </div>
  );
}

export default function MailboxScreen({ onBack }: MailboxScreenProps) {
  const { client } = useVybeTraderClient();
  const messages = useMailboxStore((s) => s.messages);
  const activeAccountKey = useMailboxStore((s) => s.activeAccountKey);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCompose, setShowCompose] = useState(false);
  const [composeTo, setComposeTo] = useState("Support");
  const [composeSubject, setComposeSubject] = useState("");
  const [filterType, setFilterType] = useState<MailboxFilterType>("all");
  const [searchQuery, setSearchQuery] = useState("");

  const rows = useMemo(
    () => messages.map((m) => toMailRow(m, activeAccountKey)),
    [messages, activeAccountKey]
  );

  const filteredRows = useMemo(
    () => filterMailRows(rows, filterType, searchQuery),
    [rows, filterType, searchQuery]
  );

  const selected = useMemo(
    () => filteredRows.find((r) => r.id === selectedId) ?? rows.find((r) => r.id === selectedId) ?? null,
    [filteredRows, rows, selectedId]
  );

  const unreadCount = useMemo(() => rows.filter((r) => r.isUnread).length, [rows]);

  const filterLabel =
    filterType === "all" ? "All" : filterType === "unread" ? "Unread" : "Support";

  const openMail = useCallback(
    (row: MailRow) => {
      setSelectedId(row.id);
      void client.markMailboxMessageRead(row.id);
      useMailboxStore.getState().markUserRead(row.id);
    },
    [client]
  );

  const handleDelete = useCallback((id: string) => {
    useMailboxStore.getState().removeMessage(id);
    setSelectedId((cur) => (cur === id ? null : cur));
  }, []);

  const handleSend = useCallback(
    async (to: string, subject: string, body: string) => {
      await client.sendMailboxMessage({
        to: to.trim() || "Support",
        subject,
        body,
      });
      setShowCompose(false);
      setComposeTo("Support");
      setComposeSubject("");
    },
    [client]
  );

  const handleReply = useCallback((row: MailRow) => {
    setComposeTo(row.from === "Support" ? "Support" : row.from);
    setComposeSubject(
      row.subject.startsWith("Re:") ? row.subject : `Re: ${row.subject}`
    );
    setSelectedId(null);
    setShowCompose(true);
  }, []);

  if (showCompose) {
    return (
      <ComposeMailScreen
        onClose={() => setShowCompose(false)}
        onSend={handleSend}
        initialTo={composeTo}
        initialSubject={composeSubject}
      />
    );
  }

  if (selected) {
    return (
      <MailDetailScreen
        mail={selected}
        onBack={() => setSelectedId(null)}
        onDelete={() => handleDelete(selected.id)}
        onReply={() => handleReply(selected)}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-background text-foreground">
      <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
        <Button variant="ghost" size="icon" onClick={onBack} className={`${DENSITY.ICON_BTN} shrink-0`}>
          <FaChevronLeft className="h-4 w-4 text-blue-400" />
        </Button>
        <span className="text-sm font-semibold">Mailbox</span>
        <span className="text-[10px] tabular-nums text-muted-foreground">
          {filteredRows.length}
          {unreadCount > 0 ? ` · ${unreadCount} unread` : ""}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className={`ml-auto ${DENSITY.ICON_BTN} shrink-0`}
          onClick={() => {
            setComposeTo("Support");
            setComposeSubject("");
            setShowCompose(true);
          }}
        >
          <PenLine className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search messages…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-9 pl-9 text-sm"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-9 shrink-0 px-3 text-xs">
              <Filter className="mr-1 h-3.5 w-3.5" />
              {filterLabel}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuLabel className="text-xs">Show</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={filterType === "all"}
              onCheckedChange={() => setFilterType("all")}
              className="text-xs"
            >
              All messages
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={filterType === "unread"}
              onCheckedChange={() => setFilterType("unread")}
              className="text-xs"
            >
              Unread only
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={filterType === "support"}
              onCheckedChange={() => setFilterType("support")}
              className="text-xs"
            >
              Support
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {filteredRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
            <Mail className="h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              {rows.length === 0 ? "No messages yet" : "No messages match your filters"}
            </p>
            {rows.length === 0 && (
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => setShowCompose(true)}
              >
                <PenLine className="mr-2 h-4 w-4" />
                Message Support
              </Button>
            )}
          </div>
        ) : (
          filteredRows.map((row) => (
            <SwipeableRow key={row.id} onDelete={() => handleDelete(row.id)}>
              <button
                type="button"
                onClick={() => openMail(row)}
                className={`flex w-full items-start gap-2 px-3 py-3 text-left active:bg-muted/40 ${
                  row.isUnread ? "bg-muted/20" : ""
                }`}
              >
                <div className="mt-0.5 flex w-4 shrink-0 flex-col items-center gap-1">
                  {row.isUnread ? (
                    <span className="h-2 w-2 rounded-full bg-primary" />
                  ) : (
                    <span className="h-2 w-2" />
                  )}
                  <DirectionIcon isSent={row.isSent} className="h-3.5 w-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1">
                    <span
                      className={`truncate text-sm ${
                        row.isUnread ? "font-semibold" : "font-medium text-foreground/90"
                      }`}
                    >
                      {listPartyLabel(row)}
                    </span>
                    <SeverityMark severity={row.severity} />
                  </div>
                  <p
                    className={`truncate text-sm ${
                      row.isUnread ? "text-foreground/90" : "text-muted-foreground"
                    }`}
                  >
                    {row.subject}
                  </p>
                  <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
                    {row.preview}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end text-[11px] leading-tight tabular-nums text-muted-foreground">
                  <span>{row.date}</span>
                  <span>{row.time}</span>
                </div>
              </button>
            </SwipeableRow>
          ))
        )}
      </div>
    </div>
  );
}
